import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Input, Select, InputNumber, DatePicker, TimePicker, Button, Row, Col, Card, List, Typography } from 'antd';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsersByOrganisation, clearOrganisationUsers } from '../../redux/userOrganisationsSlice';
import ConfirmModal from '../ConfirmModal';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

const workSchedules = [
    '6/1', '5/2', '4/4', '4/3', '4/2', '3/3', '2/2', '1/3', '1/2',
    'По выходным', 'Свободный', 'Другое'
];

const vacancyReasons = [
    'Новая вакансия',
    'Внутренний перевод',
    'Декрет',
    'Замена'
];

const CreateFromTemplateModal = ({
    isOpen,
    onClose,
    onSubmit,
    vacanciesTemplate,
    organisations,
    users,
}) => {
    const dispatch = useDispatch();
    const { organisationUsers } = useSelector((state) => state.userOrganisations);
    const [form] = Form.useForm();
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [filteredDepartments, setFilteredDepartments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [draftLoading, setDraftLoading] = useState(false);
    const [isDraftSaved, setIsDraftSaved] = useState(false);
    const [isHiddenVacancy, setIsHiddenVacancy] = useState(false);
    const [businessTripsRequired, setBusinessTripsRequired] = useState(false);
    const [relocationWillingness, setRelocationWillingness] = useState(false);
    const [communicationCompensation, setCommunicationCompensation] = useState(false);
    const [hasCar, setHasCar] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [initialFormValues, setInitialFormValues] = useState({});

    const driverLicenseRequiredValue = Form.useWatch('driver_license_required', form);
    const selectedOrganisationId = Form.useWatch('organisation_id', form);

    useEffect(() => {
        if (!isOpen) {
            form.resetFields();
            setSelectedTemplate(null);
            setShowForm(false);
            setIsDraftSaved(false);
            setIsHiddenVacancy(false);
            setBusinessTripsRequired(false);
            setRelocationWillingness(false);
            setCommunicationCompensation(false);
            setHasCar(false);
            dispatch(clearOrganisationUsers());
        }
    }, [isOpen, form, dispatch]);

    const filterDepartments = useCallback((companyId) => {
        const numericCompanyId = typeof companyId === 'string' ? parseInt(companyId, 10) : companyId;
        const organisation = organisations.find(org => org.organisation_id === numericCompanyId);
        if (organisation && organisation.departments) {
            setFilteredDepartments(organisation.departments);
        } else {
            setFilteredDepartments([]);
        }
    }, [organisations]);

    useEffect(() => {
        if (organisations.length > 0) {
            const organisationId = form.getFieldValue('organisation_id');
            if (organisationId) {
                filterDepartments(organisationId);
            }
        }
    }, [organisations, form, filterDepartments]);

    const handleCompanyChange = async (orgId) => {
        form.setFieldsValue({ department_id: undefined, assigner_id: undefined });
        try {
            filterDepartments(orgId);
        } catch (error) {
            console.error('Error filtering departments:', error);
            setFilteredDepartments([]);
        }
    };

    useEffect(() => {
        if (selectedOrganisationId) {
            dispatch(fetchUsersByOrganisation(selectedOrganisationId));
        } else {
            dispatch(clearOrganisationUsers());
        }
    }, [selectedOrganisationId, dispatch]);

    const handleCheckboxToggle = (fieldName) => {
        const currentValue = form.getFieldValue(fieldName) || false;
        form.setFieldsValue({ [fieldName]: !currentValue });
    };

    const getValidationRules = (fieldName, isRequired = false) => {
        const rules = [];
        if (isRequired) {
            rules.push({ required: true, message: 'Это поле обязательно' });
        }

        if (fieldName.includes('salary')) {
            rules.push({
                validator: (_, value) => {
                    if (value && value < 0) {
                        return Promise.reject(new Error('Зарплата не может быть отрицательной'));
                    }
                    return Promise.resolve();
                }
            });
        }

        return rules;
    };

    const checkForChanges = useCallback(() => {
        if (!showForm) return false;
        const currentValues = form.getFieldsValue(true);
        return JSON.stringify(currentValues) !== JSON.stringify(initialFormValues);
    }, [form, initialFormValues, showForm]);

    const handleFormChange = useCallback(() => {
        setHasUnsavedChanges(checkForChanges());
    }, [checkForChanges]);

    const handleTemplateSelect = (template) => {
        setSelectedTemplate(template);
        setShowForm(true);

        if (template.organisation_id) {
            handleCompanyChange(template.organisation_id);
        }

        const formData = {
            organisation_id: template.organisation_id || null,
            department_id: template.department_id || null,
            city: template.city || '',
            creator_id: template.creator_id || null,
            creator_position: template.creator_position || '',
            assigner_id: template.assigner_id || null,
            supervisors: template.supervisors || '',
            job_title: template.job_title || '',
            count: template.positions_count || 1,
            rate: template.rate || '1',
            vacancy_reason: template.vacancy_reason || null,
            subordinate_count: template.subordinates_count || 0,
            job_address: template.workplace_address || '',
            opening_date: template.opening_date ? dayjs(template.opening_date) : dayjs(),
            planned_close_date: template.planned_close_date ? dayjs(template.planned_close_date) : null,
            planned_start_date: template.planned_start_date ? template.planned_start_date : null,
            work_start_time: template.work_start_time ? dayjs(template.work_start_time, 'HH:mm') : null,
            work_end_time: template.work_end_time ? dayjs(template.work_end_time, 'HH:mm') : null,
            work_location_ratio: template.field_vs_office || '',
            business_trips_required: template.requires_travel || false,
            business_trips_details: template.travel_details || '',
            relocation_willingness: template.requires_relocation || false,
            communication_compensation: template.compensate_phone || false,
            car_compensation: template.compensate_car || false,
            tasks: template.tasks || '',
            expected_results: template.expected_results || '',
            responsibilities: template.duties || '',
            reporting: template.reporting || '',
            salary_structure: template.salary_structure || '',
            net_probation_salary_min: template.trial_salary?.min || undefined,
            net_probation_salary_max: template.trial_salary?.max || undefined,
            net_post_probation_salary_min: template.post_trial_salary?.min || undefined,
            net_post_probation_salary_max: template.post_trial_salary?.max || undefined,
            gender: template.gender || null,
            min_age: template.min_age || undefined,
            max_age: template.max_age || undefined,
            marital_status: template.marital_status || [],
            driver_license_required: template.requires_license || false,
            driver_license_categories: template.license_categories || [],
            has_car: template.requires_car || false,
            education: template.education_levels || [],
            pc_skills: template.computer_skills_level || '',
            skills_1c: template.skills_1c || '',
            software_knowledge: template.software_skills?.map(sk => ({
                program: sk.program || sk,
                level: sk.level || 'Не требуется'
            })) || [],
            language_knowledge: template.language_skills?.map(lang => ({
                language: lang.language || lang,
                level: lang.level || 'Не требуется'
            })) || [],
            work_experience: template.experience_description || '',
            personal_qualities: template.personal_traits || '',
            professional_skills: template.professional_skills || '',
            preferred_companies: template.preferred_companies || '',
            unwanted_companies: template.unwanted_companies || '',
            other_conditions: template.other_conditions || '',
        };

        // Устанавливаем состояния кнопок-чекбоксов
        setIsHiddenVacancy(template.is_hidden || false);
        setBusinessTripsRequired(template.requires_travel || false);
        setRelocationWillingness(template.requires_relocation || false);
        setCommunicationCompensation(template.compensate_phone || false);
        setHasCar(template.requires_car || false);

        setTimeout(() => {
            form.setFieldsValue(formData);
            setInitialFormValues(formData);
            setHasUnsavedChanges(false);
        }, 100);
    };

    const handleSaveDraft = async () => {
        try {
            setDraftLoading(true);
            // Черновик: сохраняем без обязательной валидации
            const values = form.getFieldsValue(true);
            values.status = 'new';
            values.is_template = true;

            // Добавляем поля для скрытой вакансии
            if (isHiddenVacancy) {
                values.is_hidden = true;
                values.visible_user_ids = values.visible_user_ids || [];
            } else {
                values.is_hidden = false;
                values.visible_user_ids = [];
            }

            // Добавляем значения чекбоксов
            values.business_trips_required = businessTripsRequired;
            values.relocation_willingness = relocationWillingness;
            values.communication_compensation = communicationCompensation;
            values.has_car = hasCar;

            const data = {
                job_title: values.job_title,
                organisation_id: values.organisation_id,
                department_id: values.department_id,
                city: values.city,
                creator_id: values.creator_id,
                creator_position: values.creator_position,
                assigner_id: values.assigner_id,
                supervisors: values.supervisors,
                count: values.count || 1,
                rate: values.rate ? String(values.rate) : '0.25',
                vacancy_reason: values.vacancy_reason,
                subordinate_count: values.subordinate_count ? String(values.subordinate_count) : '0',
                job_address: values.job_address,
                opening_date: values.opening_date ? dayjs(values.opening_date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
                planned_close_date: values.planned_close_date ? dayjs(values.planned_close_date).format('YYYY-MM-DD') : null,
                planned_start_date: values.planned_start_date ? values.planned_start_date : null,
                work_start_time: values.work_start_time ? values.work_start_time.format('HH:mm') : null,
                work_end_time: values.work_end_time ? values.work_end_time.format('HH:mm') : null,
                work_schedule: values.work_schedule || null,
                work_location_ratio: values.work_location_ratio,
                requires_travel: values.business_trips_required || false,
                travel_details: values.business_trips_details,
                requires_relocation: values.relocation_willingness || false,
                compensate_phone: values.communication_compensation || false,
                compensate_car: values.car_compensation || false,
                tasks: values.tasks,
                expected_results: values.expected_results,
                duties: values.responsibilities,
                reporting: values.reporting,
                gender: values.gender,
                min_age: values.min_age,
                max_age: values.max_age,
                marital_status: values.marital_status || [],
                requires_license: driverLicenseRequiredValue,
                license_categories: values.driver_license_categories || [],
                requires_car: values.has_car || false,
                education_levels: values.education || [],
                skills_1c: values.skills_1c,
                software_skills: (values.software_knowledge || [])
                    .filter(sk => sk && sk.program && sk.program.trim() !== '')
                    .map(sk => ({
                        program: sk.program.trim(),
                        level: sk.level || 'Не требуется'
                    })),
                language_skills: (values.language_knowledge || [])
                    .filter(lang => lang && lang.language && lang.language.trim() !== '')
                    .map(lang => ({
                        language: lang.language.trim(),
                        level: lang.level || 'Не требуется'
                    })),
                experience_description: values.work_experience,
                personal_traits: values.personal_qualities,
                professional_skills: values.professional_skills,
                preferred_companies: values.preferred_companies,
                unwanted_companies: values.unwanted_companies,
                other_conditions: values.other_conditions,
                salary_structure: values.salary_structure,
                status: 'new',
                is_template: true,
                is_hidden: values.is_hidden,
                visible_user_ids: values.visible_user_ids || []
            };

            if (values.net_probation_salary_min || values.net_probation_salary_max) {
                data.trial_salary = `${values.net_probation_salary_min || ''}${values.net_probation_salary_min && values.net_probation_salary_max ? '-' : ''}${values.net_probation_salary_max || ''}`;
            }

            if (values.net_post_probation_salary_min || values.net_post_probation_salary_max) {
                data.post_trial_salary = `${values.net_post_probation_salary_min || ''}${values.net_post_probation_salary_min && values.net_post_probation_salary_max ? '-' : ''}${values.net_post_probation_salary_max || ''}`;
            }

            if (values.pc_skills) {
                data.computer_skills_level = values.pc_skills;
            }

            await onSubmit(selectedTemplate.id, data.organisation_id, data);
            setIsDraftSaved(true);
        } catch (error) {
            console.error('Draft save failed:', error);
            if (error?.errorFields?.length > 0) {
                form.scrollToField(error.errorFields[0].name, {
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        } finally {
            setDraftLoading(false);
        }
    };

    const handleDeleteDraft = () => {
        setIsDraftSaved(false);
        form.resetFields();
    };

    const handleFinish = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();

            // Добавляем поля для скрытой вакансии
            if (isHiddenVacancy) {
                values.is_hidden = true;
                values.visible_user_ids = values.visible_user_ids || [];
            } else {
                values.is_hidden = false;
                values.visible_user_ids = [];
            }

            // Добавляем значения чекбоксов
            values.business_trips_required = businessTripsRequired;
            values.relocation_willingness = relocationWillingness;
            values.communication_compensation = communicationCompensation;
            values.has_car = hasCar;

            const data = {
                job_title: values.job_title,
                organisation_id: values.organisation_id,
                department_id: values.department_id,
                city: values.city,
                creator_id: values.creator_id,
                creator_position: values.creator_position,
                assigner_id: values.assigner_id,
                supervisors: values.supervisors,
                count: values.count || 1,
                rate: values.rate ? String(values.rate) : '0.25',
                vacancy_reason: values.vacancy_reason,
                subordinate_count: values.subordinate_count ? String(values.subordinate_count) : '0',
                job_address: values.job_address,
                opening_date: values.opening_date ? dayjs(values.opening_date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
                planned_close_date: values.planned_close_date ? dayjs(values.planned_close_date).format('YYYY-MM-DD') : null,
                planned_start_date: values.planned_start_date ? values.planned_start_date : null,
                work_start_time: values.work_start_time ? values.work_start_time.format('HH:mm') : null,
                work_end_time: values.work_end_time ? values.work_end_time.format('HH:mm') : null,
                work_schedule: values.work_schedule || null,
                work_location_ratio: values.work_location_ratio,
                requires_travel: values.business_trips_required || false,
                travel_details: values.business_trips_details,
                requires_relocation: values.relocation_willingness || false,
                compensate_phone: values.communication_compensation || false,
                compensate_car: values.car_compensation || false,
                tasks: values.tasks,
                expected_results: values.expected_results,
                duties: values.responsibilities,
                reporting: values.reporting,
                gender: values.gender,
                min_age: values.min_age,
                max_age: values.max_age,
                marital_status: values.marital_status || [],
                requires_license: driverLicenseRequiredValue,
                license_categories: values.driver_license_categories || [],
                requires_car: values.has_car || false,
                education_levels: values.education || [],
                skills_1c: values.skills_1c,
                software_skills: (values.software_knowledge || [])
                    .filter(sk => sk && sk.program && sk.program.trim() !== '')
                    .map(sk => ({
                        program: sk.program.trim(),
                        level: sk.level || 'Не требуется'
                    })),
                language_skills: (values.language_knowledge || [])
                    .filter(lang => lang && lang.language && lang.language.trim() !== '')
                    .map(lang => ({
                        language: lang.language.trim(),
                        level: lang.level || 'Не требуется'
                    })),
                experience_description: values.work_experience,
                personal_traits: values.personal_qualities,
                professional_skills: values.professional_skills,
                preferred_companies: values.preferred_companies,
                unwanted_companies: values.unwanted_companies,
                other_conditions: values.other_conditions,
                salary_structure: values.salary_structure,
                status: 'new',
                is_template: false,
                is_hidden: values.is_hidden,
                visible_user_ids: values.visible_user_ids || []
            };

            if (values.net_probation_salary_min || values.net_probation_salary_max) {
                data.trial_salary = `${values.net_probation_salary_min || ''}${values.net_probation_salary_min && values.net_probation_salary_max ? '-' : ''}${values.net_probation_salary_max || ''}`;
            }

            if (values.net_post_probation_salary_min || values.net_post_probation_salary_max) {
                data.post_trial_salary = `${values.net_post_probation_salary_min || ''}${values.net_post_probation_salary_min && values.net_post_probation_salary_max ? '-' : ''}${values.net_post_probation_salary_max || ''}`;
            }

            if (values.pc_skills) {
                data.computer_skills_level = values.pc_skills;
            }
            await onSubmit(selectedTemplate.id, data.organisation_id, data);
            form.resetFields();
            setSelectedTemplate(null);
            setShowForm(false);
        } catch (error) {
            console.error('Ошибка валидации формы:', error);
            if (error?.errorFields?.length > 0) {
                form.scrollToField(error.errorFields[0].name, {
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        if (hasUnsavedChanges) {
            setShowConfirm(true);
        } else {
            performBack();
        }
    };

    const performBack = useCallback(() => {
        setShowForm(false);
        setSelectedTemplate(null);
        form.resetFields();
        setIsDraftSaved(false);
        setIsHiddenVacancy(false);
        setBusinessTripsRequired(false);
        setRelocationWillingness(false);
        setCommunicationCompensation(false);
        setHasCar(false);
        setHasUnsavedChanges(false);
        setInitialFormValues({});
    }, [form]);

    const handleModalCancel = useCallback(() => {
        if (hasUnsavedChanges) {
            setShowConfirm(true);
        } else {
            onClose();
        }
    }, [hasUnsavedChanges, onClose]);

    const handleConfirmClose = useCallback((shouldClose) => {
        if (shouldClose) {
            if (showForm) {
                performBack();
            } else {
                onClose();
            }
        }
        setShowConfirm(false);
    }, [showForm, onClose, performBack]);

    const renderTemplateList = () => (
        <div>
            <Title level={4}>Выберите черновик вакансии</Title>
            <List
                dataSource={vacanciesTemplate}
                renderItem={(template) => (
                    <List.Item>
                        <Card
                            hoverable
                            style={{ width: '100%', cursor: 'pointer' }}
                            onClick={() => handleTemplateSelect(template)}
                        >
                            <div>
                                <Title level={5}>{template.job_title || `Черновик #${template.id}`}</Title>
                                <Text type="secondary">
                                    {template.positions_count ? `${template.positions_count} позиций` : ''}
                                    {template.organisation_id && organisations.find(org => org.organisation_id === template.organisation_id)?.name &&
                                        ` • ${organisations.find(org => org.organisation_id === template.organisation_id).name}`}
                                </Text>
                            </div>
                        </Card>
                    </List.Item>
                )}
            />
        </div>
    );

    const renderForm = () => (
        <div>
            <div style={{ marginBottom: 16 }}>
                <Button onClick={handleBack} style={{ marginBottom: 16 }}>
                    ← Назад к списку черновиков
                </Button>
                {selectedTemplate && (
                    <div style={{ marginBottom: 16 }}>
                        <Text strong>Выбранный черновик: {selectedTemplate.job_title || `Черновик #${selectedTemplate.id}`}</Text>
                    </div>
                )}
            </div>

            <Form form={form} layout="vertical" onFinish={handleFinish} onFieldsChange={handleFormChange} scrollToFirstError={{ behavior: 'smooth', block: 'center' }}>
                {/* Основная информация о вакансии */}
                <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                        Основная информация
                    </h3>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="job_title" label="Название должности" rules={[{ required: true, message: 'Укажите название должности' }]}>
                                <Input />
                            </Form.Item>

                            <Form.Item name="organisation_id" label="Компания" rules={[{ required: true, message: 'Выберите компанию' }]}>
                                <Select
                                    showSearch
                                    optionFilterProp="children"
                                    placeholder="Выберите компанию"
                                    onChange={handleCompanyChange}
                                >
                                    {organisations?.map(org => (
                                        <Option key={org.id} value={org.organisation_id}>{org.name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item name="department_id" label="Отдел" rules={[{ required: true, message: 'Выберите отдел' }]}>
                                <Select
                                    showSearch
                                    optionFilterProp="children"
                                    placeholder="Выберите отдел"
                                >
                                    {filteredDepartments?.map(dept => (
                                        <Option key={dept.id} value={dept.id}>{dept.name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item name="city" label="Город" rules={[{ required: true, message: 'Укажите город' }]}>
                                <Input />
                            </Form.Item>

                            <Form.Item name="job_address" label="Адрес рабочего места" rules={[{ required: true, message: 'Укажите адрес рабочего места' }]}>
                                <Input />
                            </Form.Item>

                            <Form.Item name="is_hidden" valuePropName="checked" label="Скрытая вакансия">
                                <Button
                                    type={isHiddenVacancy ? "primary" : "default"}
                                    onClick={() => setIsHiddenVacancy(!isHiddenVacancy)}
                                    style={{
                                        width: '100%',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: isHiddenVacancy ? 'bold' : 'normal'
                                    }}
                                >
                                    {isHiddenVacancy ? '✓ Скрытая вакансия' : 'Скрытая вакансия'}
                                </Button>
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="count" label="Количество позиций" rules={[{ required: true, message: 'Укажите количество позиций' }]}>
                                <InputNumber min={1} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item name="rate" label="Ставка" rules={[{ required: true, message: 'Выберите ставку' }]}>
                                <Select placeholder="Выберите ставку">
                                    {Array.from({ length: 8 }, (_, i) => (0.25 * (i + 1)).toFixed(2)).map(value => (
                                        <Option key={value} value={String(value)}>
                                            {value}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item name="vacancy_reason" label="Причина возникновения вакансии" rules={[{ required: true, message: 'Выберите причину возникновения вакансии' }]}>
                                <Select>
                                    {vacancyReasons.map(reason => (
                                        <Option key={reason} value={reason}>{reason}</Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item name="subordinate_count" label="Подчиненные (количество)" rules={[{ required: true, message: 'Укажите количество подчиненных' }]}>
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>

                            <Form.Item name="planned_close_date" label="Планируемая дата закрытия вакансии">
                                <DatePicker
                                    style={{ width: '100%' }}
                                    showTime={false}
                                    format="YYYY-MM-DD"
                                />
                            </Form.Item>


                            {isHiddenVacancy && (
                                <Form.Item
                                    name="visible_user_ids"
                                    label="Пользователи, которые смогут видеть вакансию"
                                    rules={[
                                        {
                                            required: isHiddenVacancy,
                                            message: 'Выберите хотя бы одного пользователя'
                                        }
                                    ]}
                                >
                                    <Select
                                        mode="multiple"
                                        showSearch
                                        optionFilterProp="children"
                                        placeholder="Выберите пользователей"
                                    >
                                        {users?.map(user => (
                                            <Option key={user.id} value={user.id}>
                                                {user.first_name} {user.last_name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            )}
                        </Col>
                    </Row>
                </div>

                {/* Ответственные лица */}
                <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                        Ответственные лица
                    </h3>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="creator_id" label="Автор заявки (ФИО)" rules={[{ required: true, message: 'Укажите автора заявки' }]}>
                                <Select showSearch optionFilterProp="children">
                                    {users?.map(u => (
                                        <Option key={u.id} value={u.id}>
                                            {u.first_name} {u.last_name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item name="creator_position" label="Автор заявки (должность)" rules={[{ required: true, message: 'Укажите должность автора заявки' }]}>
                                <Input />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="assigner_id" label="Рекрутер">
                                <Select showSearch optionFilterProp="children">
                                    {selectedOrganisationId && organisationUsers?.length > 0
                                        ? (() => {
                                            // Фильтруем уникальных пользователей по user_id
                                            const uniqueUsers = [];
                                            const seenUserIds = new Set();
                                            organisationUsers.forEach(u => {
                                                const userId = u.user_id || u.id;
                                                if (!seenUserIds.has(userId)) {
                                                    seenUserIds.add(userId);
                                                    uniqueUsers.push(u);
                                                }
                                            });
                                            return uniqueUsers.map(u => (
                                                <Option key={u.user_id || u.id} value={u.user_id || u.id}>
                                                    {u.first_name} {u.last_name}
                                                </Option>
                                            ));
                                        })()
                                        : users?.map(u => (
                                            <Option key={u.id} value={u.id}>
                                                {u.first_name} {u.last_name}
                                            </Option>
                                        ))
                                    }
                                </Select>
                            </Form.Item>

                            <Form.Item name="supervisors" label="Начальник">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>

                {/* Даты и время */}
                <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                        Даты и время
                    </h3>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="opening_date" label="Дата открытия вакансии" rules={[{ required: true, message: 'Укажите дату открытия вакансии' }]}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="planned_start_date" label="Планируемая дата выхода сотрудника на работу" rules={[{ required: true, message: 'Укажите планируемую дату выхода сотрудника на работу' }]}>
                                <Input placeholder="Можно указать дату, месяц или период (например: 01.09.2023, сентябрь 2023, 3-й квартал 2023)" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="work_start_time" label="Время начала работы">
                                <TimePicker
                                    style={{ width: '100%' }}
                                    format="HH:mm"
                                    needConfirm={false}
                                    placeholder="Выберите время"
                                />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="work_end_time" label="Время окончания работы">
                                <TimePicker
                                    style={{ width: '100%' }}
                                    format="HH:mm"
                                    needConfirm={false}
                                    placeholder="Выберите время"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="work_schedule" label="График работы" rules={[{ required: true, message: 'Выберите график работы' }]}>
                                <Select placeholder="Выберите график работы">
                                    {workSchedules.map(schedule => (
                                        <Option key={schedule} value={schedule}>{schedule}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="work_location_ratio" label="Соотношение работы: дом/офис">
                                <Input placeholder="Например: 70% офис, 30% дом" />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>

                {/* Условия работы */}
                <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                        Условия работы
                    </h3>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="communication_compensation" valuePropName="checked" label="Компенсация затрат: связь">
                                <Button
                                    type={communicationCompensation ? "primary" : "default"}
                                    onClick={() => setCommunicationCompensation(!communicationCompensation)}
                                    style={{
                                        width: '100%',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: communicationCompensation ? 'bold' : 'normal'
                                    }}
                                >
                                    {communicationCompensation ? '✓ Компенсация затрат: связь' : 'Компенсация затрат: связь'}
                                </Button>
                            </Form.Item>

                            <Form.Item name="car_compensation" valuePropName="checked" label="Компенсация затрат: личный автомобиль">
                                <Button
                                    type={hasCar ? "primary" : "default"}
                                    onClick={() => setHasCar(!hasCar)}
                                    style={{
                                        width: '100%',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: hasCar ? 'bold' : 'normal'
                                    }}
                                >
                                    {hasCar ? '✓ Компенсация затрат: личный автомобиль' : 'Компенсация затрат: личный автомобиль'}
                                </Button>
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="business_trips_required" valuePropName="checked" label="Командировки">
                                <Button
                                    type={businessTripsRequired ? "primary" : "default"}
                                    onClick={() => setBusinessTripsRequired(!businessTripsRequired)}
                                    style={{
                                        width: '100%',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: businessTripsRequired ? 'bold' : 'normal'
                                    }}
                                >
                                    {businessTripsRequired ? '✓ Командировки' : 'Командировки'}
                                </Button>
                            </Form.Item>

                            {businessTripsRequired && (
                                <Form.Item name="business_trips_details" label="Частота и длительность командировок" rules={[{ required: true, message: 'Укажите частоту и длительность командировок' }]}>
                                    <Input placeholder="Например: 2 раза в месяц, 3-5 дней" />
                                </Form.Item>
                            )}

                            <Form.Item name="relocation_willingness" valuePropName="checked" label="Готовность к переезду">
                                <Button
                                    type={relocationWillingness ? "primary" : "default"}
                                    onClick={() => setRelocationWillingness(!relocationWillingness)}
                                    style={{
                                        width: '100%',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: relocationWillingness ? 'bold' : 'normal'
                                    }}
                                >
                                    {relocationWillingness ? '✓ Готовность к переезду' : 'Готовность к переезду'}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </div>

                {/* Описание работы */}
                <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                        Описание работы
                    </h3>

                    <Form.Item name="tasks" label="Задачи" rules={[{ required: true, message: 'Опишите задачи' }]}>
                        <TextArea rows={3} />
                    </Form.Item>

                    <Form.Item name="expected_results" label="Ожидаемые результаты" rules={[{ required: true, message: 'Опишите ожидаемые результаты' }]}>
                        <TextArea rows={3} />
                    </Form.Item>

                    <Form.Item name="responsibilities" label="Функциональные обязанности" rules={[{ required: true, message: 'Опишите функциональные обязанности' }]}>
                        <TextArea rows={3} />
                    </Form.Item>

                    <Form.Item name="reporting" label="Отчетность о проделанной работе">
                        <TextArea rows={3} />
                    </Form.Item>

                </div>

                {/* Зарплата */}
                <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                        Зарплата
                    </h3>
                    <Form.Item name="salary_structure" label="Структура заработной платы">
                        <TextArea rows={2} />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Зарплата на испытательном сроке (BYN)"
                                validateStatus={form.getFieldError('net_probation_salary_min')?.length > 0 || form.getFieldError('net_probation_salary_max')?.length > 0 ? 'error' : ''}
                                help={form.getFieldError('net_probation_salary_min')?.[0] || form.getFieldError('net_probation_salary_max')?.[0]}
                            >
                                <Input.Group compact>
                                    <Form.Item name="net_probation_salary_min" noStyle rules={getValidationRules('net_probation_salary_min', false)}>
                                        <InputNumber
                                            min={0}
                                            placeholder="От"
                                            style={{ width: '50%' }}
                                            parser={value => value?.replace(/\s?BYN/g, '')}
                                        />
                                    </Form.Item>
                                    <Form.Item name="net_probation_salary_max" noStyle rules={getValidationRules('net_probation_salary_max', false)}>
                                        <InputNumber
                                            min={0}
                                            placeholder="До"
                                            style={{ width: '50%' }}
                                            parser={value => value?.replace(/\s?BYN/g, '')}
                                        />
                                    </Form.Item>
                                </Input.Group>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Зарплата после испытательного срока (BYN)"
                                validateStatus={form.getFieldError('net_post_probation_salary_min')?.length > 0 || form.getFieldError('net_post_probation_salary_max')?.length > 0 ? 'error' : ''}
                                help={form.getFieldError('net_post_probation_salary_min')?.[0] || form.getFieldError('net_post_probation_salary_max')?.[0]}
                            >
                                <Input.Group compact>
                                    <Form.Item name="net_post_probation_salary_min" noStyle>
                                        <InputNumber
                                            min={0}
                                            placeholder="От"
                                            style={{ width: '50%' }}
                                            parser={value => value?.replace(/\s?BYN/g, '')}
                                        />
                                    </Form.Item>
                                    <Form.Item name="net_post_probation_salary_max" noStyle>
                                        <InputNumber
                                            min={0}
                                            placeholder="До"
                                            style={{ width: '50%' }}
                                            parser={value => value?.replace(/\s?BYN/g, '')}
                                        />
                                    </Form.Item>
                                </Input.Group>
                            </Form.Item>
                        </Col>
                    </Row>
                </div>

                {/* Требования к кандидату */}
                <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                        Требования к кандидату
                    </h3>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="gender" label="Пол">
                                <Select>
                                    <Option value="Мужской">Мужской</Option>
                                    <Option value="Женский">Женский</Option>
                                    <Option value="Не имеет значения">Не имеет значения</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="min_age" label="Минимальный возраст">
                                <InputNumber min={18} max={100} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="max_age" label="Максимальный возраст">
                                <InputNumber min={18} max={100} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="marital_status" label="Семейное положение">
                                <Select
                                    mode="multiple"
                                    options={[
                                        { value: 'Холост/Не замужем', label: 'Холост/Не замужем' },
                                        { value: 'Женат/Замужем', label: 'Женат/Замужем' },
                                        { value: 'Разведен(а)', label: 'Разведен(а)' },
                                        { value: 'Вдовец/Вдова', label: 'Вдовец/Вдова' },
                                        { value: 'Не имеет значения', label: 'Не имеет значения' },
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="education" label="Образование">
                                <Select
                                    mode="multiple"
                                    options={[
                                        'Базовое',
                                        'Среднее',
                                        'Профессионально-техническое',
                                        'Среднее специальное',
                                        'Высшее'
                                    ].map(ed => ({ value: ed, label: ed }))}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            {/* Водительское удостоверение */}
                            <Form.Item name="driver_license_required" valuePropName="checked" style={{ display: 'none' }}>
                                <Input type="hidden" />
                            </Form.Item>
                            <Form.Item label="Наличие водительского удостоверения">
                                <Button
                                    type={driverLicenseRequiredValue ? "primary" : "default"}
                                    onClick={() => handleCheckboxToggle('driver_license_required')}
                                    style={{
                                        width: '100%',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: driverLicenseRequiredValue ? 'bold' : 'normal'
                                    }}
                                >
                                    {driverLicenseRequiredValue ? '✓ Наличие водительского удостоверения' : 'Наличие водительского удостоверения'}
                                </Button>
                            </Form.Item>

                            {driverLicenseRequiredValue && (
                                <Form.Item name="driver_license_categories" label="Категории водительских прав">
                                    <Select
                                        mode="multiple"
                                        options={[
                                            'A', 'A1', 'B', 'B1', 'C', 'C1', 'D', 'D1', 'BE', 'CE', 'C1E', 'DE', 'D1E'
                                        ].map(cat => ({ value: cat, label: cat }))}
                                    />
                                </Form.Item>
                            )}
                        </Col>
                        <Col span={12}>
                            <Form.Item name="has_car" valuePropName="checked" label="Наличие личного автомобиля">
                                <Button
                                    type={hasCar ? "primary" : "default"}
                                    onClick={() => setHasCar(!hasCar)}
                                    style={{
                                        width: '100%',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: hasCar ? 'bold' : 'normal'
                                    }}
                                >
                                    {hasCar ? '✓ Наличие личного автомобиля' : 'Наличие личного автомобиля'}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </div>

                {/* Навыки и опыт */}
                <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                        Навыки и опыт
                    </h3>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="pc_skills" label="Уровень владения ПК (Word, Excel и другие)">
                                <Select
                                    options={[
                                        'Не требуется',
                                        'Базовый',
                                        'Уверенный',
                                        'Продвинутый'
                                    ].map(level => ({ value: level, label: level }))}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="skills_1c" label="Навыки в работе с 1С">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.List name="software_knowledge">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Row gutter={8} key={key}>
                                        <Col span={12}>
                                            <Form.Item {...restField} name={[name, 'program']} label="Программа">
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col span={10}>
                                            <Form.Item {...restField} name={[name, 'level']} label="Уровень владения">
                                                <Select options={[
                                                    'Базовый',
                                                    'Уверенный',
                                                    'Продвинутый'
                                                ].map(l => ({ value: l, label: l }))} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={2} style={{ marginTop: '6px', display: 'flex', alignItems: 'center' }}>
                                            <Button danger onClick={() => remove(name)}>Удалить</Button>
                                        </Col>
                                    </Row>
                                ))}
                                <Form.Item>
                                    <Button type="dashed" onClick={() => add()} block>
                                        Добавить программу
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>

                    <Form.List name="language_knowledge">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Row gutter={8} key={key}>
                                        <Col span={12}>
                                            <Form.Item {...restField} name={[name, 'language']} label="Язык">
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col span={10}>
                                            <Form.Item {...restField} name={[name, 'level']} label="Уровень владения">
                                                <Select options={[
                                                    'Базовый',
                                                    'Уверенный',
                                                    'Продвинутый'
                                                ].map(l => ({ value: l, label: l }))} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={2} style={{ marginTop: '6px', display: 'flex', alignItems: 'center' }}>
                                            <Button danger onClick={() => remove(name)}>Удалить</Button>
                                        </Col>
                                    </Row>
                                ))}
                                <Form.Item>
                                    <Button type="dashed" onClick={() => add()} block>
                                        Добавить язык
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>

                    <Form.Item name="work_experience" label="Опыт работы (область, продолжительность)">
                        <TextArea rows={2} />
                    </Form.Item>
                </div>

                {/* Личные и профессиональные качества */}
                <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                        Личные и профессиональные качества
                    </h3>
                    <Form.Item name="personal_qualities" label="Личные качества кандидатов">
                        <TextArea rows={3} />
                    </Form.Item>

                    <Form.Item name="professional_skills" label="Профессиональные навыки кандидатов">
                        <TextArea rows={3} />
                    </Form.Item>
                </div>

                {/* Дополнительные требования */}
                <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                        Дополнительные требования
                    </h3>
                    <Form.Item name="preferred_companies" label="Компании, из которых предпочтительно рассматривать кандидатов">
                        <TextArea rows={2} />
                    </Form.Item>

                    <Form.Item name="unwanted_companies" label="Компании, из которых нежелательно рассматривать кандидатов">
                        <TextArea rows={2} />
                    </Form.Item>

                    <Form.Item name="other_conditions" label="Другие условия">
                        <TextArea rows={3} />
                    </Form.Item>
                </div>
            </Form >
        </div >
    );

    const footerButtons = showForm ? [
        <Button key="cancel" onClick={handleBack}>Назад</Button>,
        !isDraftSaved ? (
            <Button key="save-draft" onClick={handleSaveDraft} loading={draftLoading}>
                Сохранить как черновик
            </Button>
        ) : (
            <Button key="delete-draft" danger onClick={handleDeleteDraft}>
                Удалить черновик
            </Button>
        ),
        <Button key="submit" type="primary" loading={loading} onClick={handleFinish}>
            Создать вакансию
        </Button>
    ] : null;

    return (
        <>
            <Modal
                open={isOpen}
                title="Создание вакансии из черновика"
                onCancel={handleModalCancel}
                footer={footerButtons}
                destroyOnClose
                width={1000}
            >
                {!showForm ? renderTemplateList() : renderForm()}
            </Modal>

            <ConfirmModal
                visible={showConfirm}
                onConfirm={handleConfirmClose}
                onCancel={() => setShowConfirm(false)}
                title="Подтверждение закрытия"
                message="Данные не будут сохранены. Продолжить?"
                okText="Продолжить"
                cancelText="Отмена"
                type="warning"
            />
        </>
    );
};

export default CreateFromTemplateModal;